// src/systems/consumables.ts
//
// Phase 15 (CONSUMABLE-02): imperative API surface for consumables.
// `useConsumable` is the ONLY invocation surface — no event-bus hooks.
// Effect defs are populated in plans 15-02, 15-03, 15-04.
// useConsumable must be called before any fiches are placed on the current
// spin (pre-placement-only guard, CONSUMABLE-03).

import type { BoardCell, Color, Consumable, ConsumableEffect } from '../state/types';
import type { GameState } from '../state/GameState';
import { RED_NUMBERS } from './wheel';
import { removeNumber } from './numbersDeck';
import { subcellConnectedComponent } from './board';

// ── Catalogue (populated by plans 15-02/03/04) ──────────────────────────

export { CONSUMABLES, CONSUMABLES_BY_ID } from '../content/consumables';
import { CONSUMABLES_BY_ID } from '../content/consumables';

// ── Instance factory (mirrors ficheFromDef in GameState.ts) ──────────────

let _consumableCounter = 0;
function nextConsumableId(): string {
  return `cons-${++_consumableCounter}`;
}

export function consumableFromDef(defId: string): Consumable {
  const def = CONSUMABLES_BY_ID[defId];
  if (!def) throw new Error(`Unknown consumable defId: ${defId}`);
  return { id: nextConsumableId(), defId, rarity: def.rarity };
}

// ── Main API ─────────────────────────────────────────────────────────────

/**
 * Apply a consumable effect and remove it from the player's inventory.
 *
 * @param effectOverride - Optional override for the effect to dispatch.
 *   Cell-targeting consumables (swapCells, flipColor, bleach, merge) require
 *   cell indices that come from UI interaction (plan 15-06). The UI resolves
 *   the real indices and passes a FULLY RESOLVED ConsumableEffect here.
 *   The def's effect carries only placeholder defaults (cellIdx: 0).
 */
export function useConsumable(gs: GameState, id: string, effectOverride?: ConsumableEffect): void {
  const consumable = gs.getState().consumables.find((c) => c.id === id);
  if (!consumable) return; // already consumed or invalid id

  const def = CONSUMABLES_BY_ID[consumable.defId];
  if (!def) {
    console.warn('[useConsumable] no def found for defId:', consumable.defId);
    return;
  }

  // Use effectOverride when provided (cell-targeting consumables supply resolved indices via UI)
  const e = effectOverride ?? def.effect;

  // WR-01 (Phase 21): flipColor and bleach have no meaningful effect on outside-sub cells.
  // Guard BEFORE removing the consumable so the player does not silently lose the item.
  // outside-sub cells have no color to flip (not a number cell) and bleach is only for
  // number cells. Return early WITHOUT consuming if the target is outside-sub.
  if (e.type === 'flipColor' || e.type === 'bleach') {
    const targetCell = gs.getState().boardLayout.cells[e.cellIdx];
    if (targetCell?.bet.kind === 'outside-sub') {
      return; // reject without consuming — cell is not a valid target for this consumable
    }
  }

  // NOTE: removeConsumable is intentionally called BEFORE the effect switch so that
  // the consumable is consumed regardless of whether the effect succeeds. Callers
  // that enforce their own UI guards (e.g. RoundScene.onConsumableTapped checking
  // isSpinning / placements.length) must provide their own visual feedback when a
  // guard fires — the system-level guard here (placements check above) is silent
  // by design: a rejected call leaves the consumable in inventory safely.
  gs.removeConsumable(id);

  switch (e.type) {
    // ── Money-class (plan 15-02) ───────────────────────────────────────
    case 'doubleOrNothing': {
      const rng = gs.getRng();
      const current = gs.getState().money;
      const doubled = Math.min(current * 2, 40);
      // rng() < 0.5 wins (seeded — deterministic per run seed; NEVER Math.random)
      const next = rng() < 0.5 ? doubled : current;
      gs.adjustMoney(next - current);
      break;
    }
    case 'looseChange':
      gs.adjustMoney(5);
      break;
    case 'compound':
      gs.setPendingInterestBonus(gs.getState().pendingInterestBonus + 5);
      break;
    // ── Cell-mutators (plan 15-03) ────────────────────────────────────
    case 'swapCells': {
      const cells = gs.getState().boardLayout.cells;
      const cellA = cells[e.cellIdxA];
      const cellB = cells[e.cellIdxB];
      if (!cellA || !cellB) break;
      // Swap bets but keep each cell at its original grid position
      const swappedA: BoardCell = { ...cellB, row: cellA.row, col: cellA.col };
      const swappedB: BoardCell = { ...cellA, row: cellB.row, col: cellB.col };
      gs.mutateBoardCell(e.cellIdxA, swappedA);
      gs.mutateBoardCell(e.cellIdxB, swappedB);
      break;
    }
    case 'flipColor': {
      const cells = gs.getState().boardLayout.cells;
      const cell = cells[e.cellIdx];
      // Guard: only straight-bet number cells; no-op on 0 (green), outside-bets
      if (!cell || cell.bet.kind !== 'straight' || cell.bet.number === 0) break;
      const currentColor: Color =
        cell.colorOverride ?? (RED_NUMBERS.has(cell.bet.number) ? 'red' : 'black');
      const newColor: Color = currentColor === 'red' ? 'black' : 'red';
      gs.mutateBoardCell(e.cellIdx, { ...cell, colorOverride: newColor });
      break;
    }
    case 'bleach': {
      const cells = gs.getState().boardLayout.cells;
      const cell = cells[e.cellIdx];
      // Guard: only straight-bet cells (number cells)
      if (!cell || cell.bet.kind !== 'straight') break;
      // CR-01 fix: set bleached flag instead of mutating bet to straight:0.
      // Using straight:0 as sentinel is indistinguishable from a real cell-0
      // bet, causing bleached cells to incorrectly score a 35:1 win when 0
      // lands. The bleached flag lets scoring skip the cell as always-losing
      // while preserving the original bet unchanged.
      gs.mutateBoardCell(e.cellIdx, { ...cell, bleached: true });
      break;
    }
    case 'merge': {
      const cells = gs.getState().boardLayout.cells;
      // CR-02 fix: guard against self-merge. If both indices are identical the
      // merge would boost a cell's payoutMult then immediately splice it out,
      // permanently losing the cell with no meaningful effect.
      if (e.cellIdxA === e.cellIdxB) break;
      const cellA = cells[e.cellIdxA];
      const cellB = cells[e.cellIdxB];
      if (!cellA || !cellB) break;
      // Boost cellA's payoutMult by cellB's payoutMult (both default to 1)
      const newMult = (cellA.payoutMult ?? 1) + (cellB.payoutMult ?? 1);
      gs.mutateBoardCell(e.cellIdxA, { ...cellA, payoutMult: newMult });
      // Remove cellB. mutateBoardCell is an in-place replace (no splice), so
      // e.cellIdxB is still valid. removeBoardCell splices, so no further
      // adjustment is needed.
      gs.removeBoardCell(e.cellIdxB);
      break;
    }
    // ── Number-deck mutators + loaded_ball (plan 15-04) ───────────────
    case 'skipSlot': {
      const result = removeNumber(gs.getState().numbersDeck, e.number);
      if (result.ok) {
        gs.mutateNumbersDeck(result.deck);
        gs.recomposeWheel();
      } else {
        console.warn('[useConsumable] skip_slot rejected:', result.reason);
      }
      break;
    }
    case 'mint': {
      const deck = gs.getState().numbersDeck;
      if (deck.length >= 50) {
        console.warn('[useConsumable] mint rejected: deck at max capacity (50)');
        break;
      }
      gs.mutateNumbersDeck([...deck, e.number]);
      gs.recomposeWheel();
      break;
    }
    case 'loadedBall':
      gs.setForcedNumber(e.number);
      break;
    case 'shuffleColumns': {
      // Shuffle whole columns 1-12 as units — each column's bets stay together.
      const rng = gs.getRng();
      const cells = gs.getState().boardLayout.cells;
      // Snapshot before any mutations
      const snap = new Map<string, BoardCell['bet']>();
      cells.forEach((c) => snap.set(`${c.row},${c.col}`, c.bet));
      // Permute column indices 1-12
      const cols = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
      for (let i = cols.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [cols[i], cols[j]] = [cols[j]!, cols[i]!];
      }
      // cols[i] = source column for destination column (i+1)
      // Rows 0 (dozen-sub) and 4 (outside-sub) stay fixed — only number rows 1-3 shuffle.
      cells.forEach((cell, idx) => {
        if (cell.col < 1 || cell.col > 12) return;
        if (cell.row === 0 || cell.row === 4) return;
        const srcCol = cols[cell.col - 1]!;
        if (srcCol === cell.col) return;
        const newBet = snap.get(`${cell.row},${srcCol}`);
        if (newBet !== undefined) gs.mutateBoardCell(idx, { ...cell, bet: newBet });
      });
      break;
    }
    case 'shuffleRows': {
      // Shuffle whole rows 0-4 as units — each row's bets stay together.
      const rng = gs.getRng();
      const cells = gs.getState().boardLayout.cells;
      // Snapshot before any mutations
      const snap = new Map<string, BoardCell['bet']>();
      cells.forEach((c) => snap.set(`${c.row},${c.col}`, c.bet));
      // Permute only number rows 1-3; rows 0 (dozen-sub) and 4 (outside-sub) stay fixed.
      const rows = [1, 2, 3];
      for (let i = rows.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [rows[i], rows[j]] = [rows[j]!, rows[i]!];
      }
      // rows[i-1] = source row for destination row i (index offset since rows=[1,2,3])
      cells.forEach((cell, idx) => {
        if (cell.row < 1 || cell.row > 3) return; // keep rows 0 and 4 fixed
        const srcRow = rows[cell.row - 1]!;
        if (srcRow === cell.row) return;
        const newBet = snap.get(`${srcRow},${cell.col}`);
        if (newBet !== undefined) gs.mutateBoardCell(idx, { ...cell, bet: newBet });
      });
      break;
    }
    case 'boostChips': {
      const cell = gs.getState().boardLayout.cells[e.cellIdx];
      if (!cell) break;
      gs.mutateBoardCell(e.cellIdx, { ...cell, chipsBonus: (cell.chipsBonus ?? 0) + 5 });
      break;
    }
    case 'boostSubcell': {
      const cell = gs.getState().boardLayout.cells[e.cellIdx];
      if (!cell) break;
      // Apply to all connected subcells (same component as the picked cell)
      const component =
        cell.bet.kind === 'dozen-sub' || cell.bet.kind === 'outside-sub'
          ? subcellConnectedComponent(gs.getState().boardLayout, cell)
          : [cell];
      const cells = gs.getState().boardLayout.cells;
      for (const member of component) {
        const memberIdx = cells.indexOf(member);
        if (memberIdx >= 0) {
          gs.mutateBoardCell(memberIdx, { ...member, chipsBonus: (member.chipsBonus ?? 0) + 5 });
        }
      }
      break;
    }
    case 'boostMult': {
      const cell = gs.getState().boardLayout.cells[e.cellIdx];
      if (!cell) break;
      gs.mutateBoardCell(e.cellIdx, { ...cell, multBonus: (cell.multBonus ?? 0) + 1 });
      break;
    }
    default: {
      const _exhaustive: never = e;
      console.error('[useConsumable] unknown effect type:', _exhaustive);
    }
  }
}
